import * as XLSX from "xlsx";

const REQUIRED_HEADER_GROUPS = [
  { label: "COMPANY or DIVISION", aliases: ["COMPANY", "DIVISION"] },
  { label: "DADRV or CODE", aliases: ["DADRV", "CODE"] },
  { label: "REFERENCE # or REFERENCE", aliases: ["REFERENCE #", "REFERENCE"] },
  { label: "GENDER", aliases: ["GENDER"] },
  { label: "NAME", aliases: ["NAME"] },
  { label: "ADDRESS", aliases: ["ADDRESS"] },
  { label: "CITY", aliases: ["CITY"] },
  { label: "STATE", aliases: ["STATE"] },
  { label: "ZIP", aliases: ["ZIP"] },
  { label: "SAFYRS", aliases: ["SAFYRS"] },
  { label: "PHONE", aliases: ["PHONE"] },
  { label: "EMAIL", aliases: ["EMAIL"] },
  { label: "Expires In", aliases: ["EXPIRES IN"] },
];

export async function validateLandstarEligibilitySpreadsheetFile(file) {
  if (!file) {
    throw new Error("Choose an .xlsx or .xls file before uploading.");
  }

  let workbook;

  try {
    const fileBuffer = await file.arrayBuffer();
    workbook = XLSX.read(fileBuffer, { type: "array" });
  } catch {
    throw new Error("Unable to read the spreadsheet. Choose a valid .xlsx or .xls file.");
  }

  const firstSheetName = workbook.SheetNames[0];

  if (!firstSheetName) {
    throw new Error("The spreadsheet does not contain any worksheets.");
  }

  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheetName], {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });
  const detectedHeaders = new Set(
    (rows[0] || []).map(normalizeHeader).filter(Boolean)
  );

  if (!detectedHeaders.size) {
    throw new Error("The spreadsheet does not contain a header row.");
  }

  const missingHeaders = REQUIRED_HEADER_GROUPS.filter(
    ({ aliases }) => !aliases.some((header) => detectedHeaders.has(header))
  ).map(({ label }) => label);

  if (missingHeaders.length) {
    throw new Error(
      `Spreadsheet headers are not valid. Missing: ${missingHeaders.join(", ")}.`
    );
  }

  const totalRows = Math.max(rows.length - 1, 0);

  if (!totalRows) {
    throw new Error("The spreadsheet contains headers but no data rows.");
  }

  const normalizedHeaders = (rows[0] || []).map(normalizeHeader);
  const driverCodeColumnIndex = normalizedHeaders.findIndex(
    (header) => header === "DADRV" || header === "CODE"
  );
  const expiresInColumnIndex = normalizedHeaders.indexOf("EXPIRES IN");
  const lNumberColumnIndex = normalizedHeaders.findIndex(
    (header) => header === "REFERENCE #" || header === "REFERENCE"
  );
  const seenLNumbers = new Set();
  let invalidLNumberRowIndex = -1;
  let duplicateLNumber = "";
  let duplicateLNumberRowIndex = -1;

  rows.slice(1).forEach((row, index) => {
    const driverCode = String(row[driverCodeColumnIndex] || "").trim();

    if (!driverCode || duplicateLNumberRowIndex >= 0) {
      return;
    }

    const lNumber = String(row[lNumberColumnIndex] || "").trim().toUpperCase();

    if (!lNumber) {
      invalidLNumberRowIndex = invalidLNumberRowIndex < 0 ? index : invalidLNumberRowIndex;
      return;
    }

    if (seenLNumbers.has(lNumber)) {
      duplicateLNumber = lNumber;
      duplicateLNumberRowIndex = index;
      return;
    }

    seenLNumbers.add(lNumber);
  });

  if (invalidLNumberRowIndex >= 0) {
    throw new Error(
      `Spreadsheet row ${invalidLNumberRowIndex + 2} must contain an L Number in REFERENCE # or REFERENCE.`
    );
  }

  if (duplicateLNumberRowIndex >= 0) {
    throw new Error(
      `Spreadsheet row ${duplicateLNumberRowIndex + 2} repeats L Number ${duplicateLNumber}. L Numbers must be unique.`
    );
  }

  const invalidExpirationRowIndex = rows.slice(1).findIndex((row) => {
    const driverCode = String(row[driverCodeColumnIndex] || "").trim();

    if (!driverCode) {
      return false;
    }

    const expiresInDays = Number(String(row[expiresInColumnIndex] || "").trim());
    return !Number.isInteger(expiresInDays) || expiresInDays <= 0;
  });

  if (invalidExpirationRowIndex >= 0) {
    throw new Error(
      `Spreadsheet row ${invalidExpirationRowIndex + 2} must contain a positive whole number in Expires In.`
    );
  }

  return {
    sheetName: firstSheetName,
    totalRows,
  };
}

function normalizeHeader(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, " ");
}
